import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Rnd } from "react-rnd";
import { chatViewport, fitChatRect, readChatRect, resizeChatFromAnchor, saveChatRect } from "@/lib/floatingChatGeometry";

export const CHAT_DRAG_HANDLE = "floating-chat-drag-handle";
const DIRECTIONS = ["top", "right", "bottom", "left", "topRight", "bottomRight", "bottomLeft", "topLeft"];

export const FloatingChatFrame = ({ testId, storageKey, initialWidth = 380, initialHeight = 540, placement = "left", children }) => {
  const [bounds, setBounds] = useState(chatViewport);
  const initialRect = () => fitChatRect({ width: initialWidth, height: initialHeight,
    x: placement === "right" ? bounds.width - initialWidth - 12 : 12,
    y: bounds.height - initialHeight - 80 }, bounds);
  const [preferred, setPreferred] = useState(() => readChatRect(storageKey, initialRect()));
  const rect = fitChatRect(preferred, bounds);

  useEffect(() => {
    const update = () => setBounds(chatViewport());
    window.addEventListener("resize", update);
    window.visualViewport?.addEventListener("resize", update);
    window.visualViewport?.addEventListener("scroll", update);
    return () => {
      window.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("scroll", update);
    };
  }, []);

  const update = (next, persist = false) => {
    const fitted = fitChatRect(next, bounds);
    setPreferred(fitted);
    if (persist) saveChatRect(storageKey, fitted);
  };
  const moveKeys = (event) => {
    const offsets = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
    if (!offsets[event.key]) return;
    event.preventDefault();
    const [x, y] = offsets[event.key];
    const step = event.shiftKey ? 40 : 10;
    update({ ...rect, x: rect.x + x * step, y: rect.y + y * step }, true);
  };
  const resize = (direction, ref, position, persist) => update({
    ...position, width: parseFloat(ref.style.width), height: parseFloat(ref.style.height),
  }, persist);

  return createPortal(
    <div data-testid={`${testId}-bounds`} className="fixed z-40 pointer-events-none" style={{ left: bounds.left, top: bounds.top, width: bounds.width, height: bounds.height }}>
      <Rnd bounds="parent" position={{ x: rect.x, y: rect.y }} size={{ width: rect.width, height: rect.height }}
        minWidth={Math.min(280, bounds.width)} minHeight={Math.min(280, bounds.height)} maxWidth={bounds.width} maxHeight={bounds.height}
        dragHandleClassName={CHAT_DRAG_HANDLE} cancel="input,textarea,select,a,[role=slider],button:not(.floating-chat-drag-handle)"
        style={{ pointerEvents: "auto" }}
        onDragStop={(_, data) => update({ ...rect, x: data.x, y: data.y }, true)}
        onResize={(_, direction, ref, delta, position) => resize(direction, ref, position, false)}
        onResizeStop={(_, direction, ref, delta, position) => resize(direction, ref, position, true)}
        resizeHandleComponent={Object.fromEntries(DIRECTIONS.map(direction => [direction,
          <span key={direction} data-testid={`${testId}-resize-${direction}`} title={`Resize chat (${direction})`} className="block h-full w-full" />]))}
      >
        <div data-testid={testId} className="h-full w-full min-w-0 min-h-0">
          {children({ rect, moveKeys, reset: () => update(initialRect(), true), resizeTo: size => update(resizeChatFromAnchor(rect, size, bounds), true) })}
        </div>
      </Rnd>
    </div>, document.body,
  );
};